import Dexie, { Table } from 'dexie'

// Define interfaces for cached data
interface CachedMasterData {
  id: string
  mrid: string
  data: unknown
  timestamp: number
  ttl: number
}

interface CachedCrossReference {
  id: string
  data: unknown
  timestamp: number
  ttl: number
}

interface CachedUserPreference {
  key: string
  value: unknown
  timestamp: number
}

interface CachedAPIResponse {
  url: string
  method: string
  params: string
  response: unknown
  timestamp: number
  ttl: number
}

// Define the database schema
class RitaCacheDB extends Dexie {
  masterData!: Table<CachedMasterData>
  crossReferences!: Table<CachedCrossReference>
  userPreferences!: Table<CachedUserPreference>
  apiResponses!: Table<CachedAPIResponse>

  constructor() {
    super('RitaCacheDB')

    // Define stores
    this.version(1).stores({
      masterData: 'id, mrid, timestamp',
      crossReferences: 'id, timestamp',
      userPreferences: 'key',
      apiResponses: '[url+method+params], timestamp',
    })
  }
}

// Create database instance
const db = new RitaCacheDB()

// Cache utilities
export const cacheUtils = {
  // Master Data caching
  async cacheMasterData(mrid: string, data: unknown, ttlMinutes: number = 30) {
    const now = Date.now()
    const ttl = ttlMinutes * 60 * 1000

    await db.masterData.put({
      id: mrid,
      mrid,
      data,
      timestamp: now,
      ttl,
    })
  },

  async getCachedMasterData(mrid: string): Promise<unknown | null> {
    const cached = await db.masterData.get(mrid)

    if (!cached) return null

    const now = Date.now()
    if (now - cached.timestamp > cached.ttl) {
      // Cache expired, delete it
      await db.masterData.delete(mrid)
      return null
    }

    return cached.data
  },

  async bulkCacheMasterData(
    items: Array<{ mrid: string; data: unknown }>,
    ttlMinutes: number = 30,
  ) {
    const now = Date.now()
    const ttl = ttlMinutes * 60 * 1000

    const cachedItems = items.map((item) => ({
      id: item.mrid,
      mrid: item.mrid,
      data: item.data,
      timestamp: now,
      ttl,
    }))

    await db.masterData.bulkPut(cachedItems)
  },

  // Cross Reference caching
  async cacheCrossReference(
    id: string,
    data: unknown,
    ttlMinutes: number = 30,
  ) {
    const now = Date.now()
    const ttl = ttlMinutes * 60 * 1000

    await db.crossReferences.put({
      id,
      data,
      timestamp: now,
      ttl,
    })
  },

  async getCachedCrossReference(id: string): Promise<unknown | null> {
    const cached = await db.crossReferences.get(id)

    if (!cached) return null

    const now = Date.now()
    if (now - cached.timestamp > cached.ttl) {
      // Cache expired, delete it
      await db.crossReferences.delete(id)
      return null
    }

    return cached.data
  },

  // User Preferences caching
  async saveUserPreference(key: string, value: unknown) {
    await db.userPreferences.put({
      key,
      value,
      timestamp: Date.now(),
    })
  },

  async getUserPreference(key: string): Promise<unknown | null> {
    const pref = await db.userPreferences.get(key)
    return pref ? pref.value : null
  },

  async getAllUserPreferences(): Promise<Record<string, unknown>> {
    const prefs = await db.userPreferences.toArray()
    return prefs.reduce(
      (acc, pref) => {
        acc[pref.key] = pref.value
        return acc
      },
      {} as Record<string, unknown>,
    )
  },

  // API Response caching
  async cacheAPIResponse(
    url: string,
    method: string,
    params: unknown,
    response: unknown,
    ttlMinutes: number = 5,
  ) {
    const now = Date.now()
    const ttl = ttlMinutes * 60 * 1000
    const paramsString = JSON.stringify(params || {})

    await db.apiResponses.put({
      url,
      method,
      params: paramsString,
      response,
      timestamp: now,
      ttl,
    })
  },

  async getCachedAPIResponse(
    url: string,
    method: string,
    params: unknown,
  ): Promise<unknown | null> {
    const paramsString = JSON.stringify(params || {})
    const cached = await db.apiResponses.get([url, method, paramsString])

    if (!cached) return null

    const now = Date.now()
    if (now - cached.timestamp > cached.ttl) {
      // Cache expired, delete it
      await db.apiResponses.delete([url, method, paramsString])
      return null
    }

    return cached.response
  },

  // Cache management
  async clearExpiredCache() {
    const now = Date.now()

    // Clear expired master data
    const expiredMasterData = await db.masterData
      .filter((item) => now - item.timestamp > item.ttl)
      .toArray()

    if (expiredMasterData.length > 0) {
      await db.masterData.bulkDelete(expiredMasterData.map((item) => item.id))
    }

    // Clear expired cross references
    const expiredCrossRefs = await db.crossReferences
      .filter((item) => now - item.timestamp > item.ttl)
      .toArray()

    if (expiredCrossRefs.length > 0) {
      await db.crossReferences.bulkDelete(
        expiredCrossRefs.map((item) => item.id),
      )
    }

    // Clear expired API responses
    const expiredAPIResponses = await db.apiResponses
      .filter((item) => now - item.timestamp > item.ttl)
      .toArray()

    if (expiredAPIResponses.length > 0) {
      await db.apiResponses.bulkDelete(
        expiredAPIResponses.map((item) => [item.url, item.method, item.params]),
      )
    }
  },

  async getCacheStats(): Promise<{
    masterDataCount: number
    crossReferenceCount: number
    apiResponseCount: number
    userPreferenceCount: number
    totalSize: number
  }> {
    const [
      masterDataCount,
      crossReferenceCount,
      apiResponseCount,
      userPreferenceCount,
    ] = await Promise.all([
      db.masterData.count(),
      db.crossReferences.count(),
      db.apiResponses.count(),
      db.userPreferences.count(),
    ])

    // Estimate size (rough approximation)
    const estimatedSize = await navigator.storage.estimate()

    return {
      masterDataCount,
      crossReferenceCount,
      apiResponseCount,
      userPreferenceCount,
      totalSize: estimatedSize.usage || 0,
    }
  },

  async clearAllCache() {
    await Promise.all([
      db.masterData.clear(),
      db.crossReferences.clear(),
      db.apiResponses.clear(),
      // Note: We don't clear user preferences
    ])
  },
}

// Auto-clear expired cache every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      cacheUtils.clearExpiredCache().catch(console.error)
    },
    5 * 60 * 1000,
  )
}
